import type { ReactNode } from 'react';
import type { RequestDetail } from '../api';
import { Alert, Badge, Button } from '../components/ui';
import { IconCheckCircle } from '../components/icons';
import type { Draft } from './draft';

const SECTION_STEP = {
  employee: 0,
  HARDWARE: 1,
  SYSTEM: 2,
  REMOTE_ACCESS: 3,
  SHAREPOINT_SITE: 4,
  FILESHARE_FOLDER: 5,
  SHARED_MAILBOX: 6,
  PRINTER: 7,
  notes: 8,
} as const;

/** Offboarding has its own order, so its jump targets do too. */
const OFF_STEP = { exit: 0, accounts: 1, revoke: 2, return: 3, handover: 4, notes: 5 } as const;

const EXIT_REASON_LABEL: Record<string, string> = {
  RESIGNATION: 'Resignation',
  TERMINATION: 'Termination',
  CONTRACT_END: 'End of contract',
  RETIREMENT: 'Retirement',
  OTHER: 'Other',
};

const MAILBOX_LABEL: Record<string, string> = {
  DISABLE: 'Disable the account',
  DELETE: 'Delete the mailbox',
  CONVERT_TO_SHARED: 'Convert to a shared mailbox',
};

const LOCATION_LABEL: Record<string, string> = { ONSITE: 'Onsite', OFFICE: 'Office', OTHER: 'Other' };

const EMPLOYMENT_TYPE_LABEL: Record<string, string> = { FIXED_TERM: 'Fixed term', PERMANENT: 'Permanent' };

export function StepReview({
  draft,
  detail,
  onJumpToStep,
  saving,
  onSubmit,
}: {
  draft: Draft;
  detail: RequestDetail;
  onJumpToStep: (index: number) => void;
  saving: boolean;
  onSubmit: () => void;
}) {
  const problems = detail.problems;
  const ready = problems.length === 0 && !saving;

  const byCategory = (category: keyof typeof SECTION_STEP) =>
    draft.items.filter((item) => item.category === category);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Review</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Check everything reads correctly. Any section can be reopened from here.
        </p>
      </div>

      {problems.length > 0 ? (
        <Alert tone="warning">
          <p className="font-medium">
            {problems.length === 1 ? 'One thing' : `${problems.length} things`} still needed before this can go to an
            approver:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {problems.map((problem, index) => (
              <li key={`${problem.field}-${index}`}>{problem.message}</li>
            ))}
          </ul>
        </Alert>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          <IconCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>This request is complete and ready to be submitted.</span>
        </div>
      )}

      {detail.type === 'OFFBOARDING' ? (
        <OffboardingSections draft={draft} detail={detail} onJumpToStep={onJumpToStep} />
      ) : (
        <EnrolmentSections draft={draft} detail={detail} onJumpToStep={onJumpToStep} />
      )}

      <div className="border-t border-slate-200 pt-6 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={onSubmit}
            disabled={!ready}
            title={ready ? undefined : 'Finish the outstanding items first'}
          >
            Submit for approval
          </Button>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            You will be asked to sign. The request then goes to the approvers, and you can no longer edit it.
          </p>
        </div>
      </div>
    </div>
  );
}

function EnrolmentSections({
  draft,
  detail,
  onJumpToStep,
}: {
  draft: Draft;
  detail: RequestDetail;
  onJumpToStep: (index: number) => void;
}) {
  const byCategory = (category: keyof typeof SECTION_STEP) =>
    draft.items.filter((item) => item.category === category);

  return (
    <>
      <Section title="Employee" onEdit={() => onJumpToStep(SECTION_STEP.employee)}>
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Full name" value={draft.employee.fullName} />
          <Row label="ID / passport" value={draft.employee.idPassport} secret />
          <Row label="Job title" value={draft.employee.jobTitle} />
          <Row label="Department" value={detail.employee?.departmentName ?? null} />
          <Row label="Manager" value={draft.employee.managerName} />
          <Row label="Start date" value={draft.employee.startDate} />
          <Row
            label="Employment type"
            value={draft.employee.employmentType ? (EMPLOYMENT_TYPE_LABEL[draft.employee.employmentType] ?? draft.employee.employmentType) : null}
          />
          {draft.employee.employmentType === 'FIXED_TERM' && (
            <>
              <Row label="Contract end date" value={draft.employee.contractEndDate} />
              <Row
                label="Taking over an existing account"
                value={
                  draft.employee.takingOverExistingAccount === null
                    ? null
                    : draft.employee.takingOverExistingAccount
                      ? draft.employee.takeoverAccountDetails || 'Yes'
                      : 'No'
                }
              />
            </>
          )}
          <Row label="Contact number" value={draft.employee.contactNumber} />
          <Row
            label="Location"
            value={
              draft.employee.locationType
                ? draft.employee.locationType === 'OTHER'
                  ? `Other: ${draft.employee.locationOther || 'not specified'}`
                  : (LOCATION_LABEL[draft.employee.locationType] ?? draft.employee.locationType)
                : null
            }
          />
          <Row
            label="Email"
            value={
              draft.employee.emailRequired
                ? draft.employee.emailAddress || 'Required: IT to assign'
                : 'Not required'
            }
          />
        </dl>
      </Section>

      <Section title="Hardware" onEdit={() => onJumpToStep(SECTION_STEP.HARDWARE)} count={byCategory('HARDWARE').length}>
        <ItemList
          items={byCategory('HARDWARE').map((item) => ({
            key: item.catalogItemId,
            label: item.name,
            detail: item.assetDetails,
          }))}
          empty="No hardware requested."
        />
      </Section>

      <Section title="Software & systems" onEdit={() => onJumpToStep(SECTION_STEP.SYSTEM)} count={byCategory('SYSTEM').length}>
        <ItemList
          items={byCategory('SYSTEM').map((item) => ({ key: item.catalogItemId, label: item.name, detail: null }))}
          empty="No systems requested."
        />
      </Section>

      <Section
        title="Remote access"
        onEdit={() => onJumpToStep(SECTION_STEP.REMOTE_ACCESS)}
        count={byCategory('REMOTE_ACCESS').length}
      >
        <ItemList
          items={byCategory('REMOTE_ACCESS').map((item) => ({
            key: item.catalogItemId,
            label: item.name,
            detail: item.notes,
          }))}
          empty="No remote access requested."
        />
      </Section>

      <Section
        title="SharePoint sites"
        onEdit={() => onJumpToStep(SECTION_STEP.SHAREPOINT_SITE)}
        count={byCategory('SHAREPOINT_SITE').length}
      >
        <ItemList
          items={byCategory('SHAREPOINT_SITE').map((item) => ({
            key: item.catalogItemId,
            label: item.name,
            detail: null,
          }))}
          empty="No SharePoint sites requested."
        />
      </Section>

      <Section
        title="Fileshare folders"
        onEdit={() => onJumpToStep(SECTION_STEP.FILESHARE_FOLDER)}
        count={byCategory('FILESHARE_FOLDER').length}
      >
        {byCategory('FILESHARE_FOLDER').length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No fileshare access requested.</p>
        ) : (
          <ul className="grid gap-x-8 sm:grid-cols-2">
            {byCategory('FILESHARE_FOLDER')
              .slice()
              .sort((a, b) => (a.legacyNumber ?? 0) - (b.legacyNumber ?? 0))
              .map((item) => (
                <li
                  key={item.catalogItemId}
                  className="flex items-center gap-2 border-b border-slate-100 py-2 text-sm dark:border-slate-800"
                >
                  <span className="w-7 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">{item.legacyNumber ?? ''}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-800 dark:text-slate-200">{item.name}</span>
                  {item.isCompulsory && <Badge tone="info">Everyone gets this</Badge>}
                  <Badge tone={item.accessLevel === 'RW' ? 'copper' : 'neutral'}>
                    {item.accessLevel === 'RW' ? 'Read & write' : 'Read only'}
                  </Badge>
                </li>
              ))}
          </ul>
        )}
      </Section>

      <Section
        title="Shared mailboxes"
        onEdit={() => onJumpToStep(SECTION_STEP.SHARED_MAILBOX)}
        count={byCategory('SHARED_MAILBOX').length}
      >
        <ItemList
          items={byCategory('SHARED_MAILBOX').map((item) => ({
            key: item.catalogItemId,
            label: item.name,
            detail: item.notes,
          }))}
          empty="No shared mailboxes requested."
        />
      </Section>

      <Section
        title="Printer access"
        onEdit={() => onJumpToStep(SECTION_STEP.PRINTER)}
        count={byCategory('PRINTER').length}
      >
        <ItemList
          items={byCategory('PRINTER').map((item) => ({ key: item.catalogItemId, label: item.name, detail: null }))}
          empty="No printer access requested."
        />
      </Section>

      <Section title="Special instructions" onEdit={() => onJumpToStep(SECTION_STEP.notes)}>
        {draft.specialInstructions ? (
          <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {draft.specialInstructions}
          </p>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">None.</p>
        )}
      </Section>
    </>
  );
}

function OffboardingSections({
  draft,
  detail,
  onJumpToStep,
}: {
  draft: Draft;
  detail: RequestDetail;
  onJumpToStep: (index: number) => void;
}) {
  const e = draft.employee;
  const access = draft.items.filter((item) => item.category !== 'HARDWARE');
  const hardware = draft.items.filter((item) => item.category === 'HARDWARE');
  const kept = access.filter((item) => item.revoke === false);

  return (
    <>
      <Section title="Who is leaving" onEdit={() => onJumpToStep(OFF_STEP.exit)}>
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Full name" value={e.fullName} />
          <Row label="Job title" value={e.jobTitle} />
          <Row label="Department" value={detail.employee?.departmentName ?? null} />
          <Row label="Manager" value={e.managerName} />
          <Row label="Email address" value={e.emailAddress} />
          <Row label="Last working day" value={e.lastWorkingDay} />
          <Row
            label="Reason"
            value={
              e.exitReason
                ? e.exitReason === 'OTHER'
                  ? `Other: ${e.exitReasonOther || 'not specified'}`
                  : (EXIT_REASON_LABEL[e.exitReason] ?? e.exitReason)
                : null
            }
          />
          <Row label="Immediate exit" value={e.immediateExit ? 'Yes: revoke on approval' : 'No'} />
        </dl>
      </Section>

      <Section title="Accounts & mailbox" onEdit={() => onJumpToStep(OFF_STEP.accounts)}>
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Mailbox" value={e.mailboxAction ? (MAILBOX_LABEL[e.mailboxAction] ?? e.mailboxAction) : null} />
          <Row label="Keep for" value={e.mailboxRetentionDays ? `${e.mailboxRetentionDays} days` : null} />
          <Row label="Forward to" value={e.forwardToName} />
          <Row label="Forward until" value={e.forwardUntil} />
          <Row label="Release licences" value={e.releaseLicences ? 'Yes' : 'No: see instructions'} />
        </dl>
        {e.outOfOfficeMessage && (
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Out-of-office message</p>
            <p className="mt-1 text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
              {e.outOfOfficeMessage}
            </p>
          </div>
        )}
      </Section>

      <Section title="Access to revoke" onEdit={() => onJumpToStep(OFF_STEP.revoke)} count={access.length}>
        {access.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nothing listed.</p>
        ) : (
          <ul className="grid gap-x-8 sm:grid-cols-2">
            {access.map((item) => (
              <li
                key={item.catalogItemId}
                className="flex items-center gap-2 border-b border-slate-100 py-2 text-sm dark:border-slate-800"
              >
                <span className="min-w-0 flex-1 truncate text-slate-800 dark:text-slate-200">{item.name}</span>
                <Badge tone={item.revoke === false ? 'warning' : 'neutral'}>
                  {item.revoke === false ? 'Kept' : 'Revoke'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
        {kept.length > 0 && (
          <div className="mt-4">
            <Alert tone="warning">
              {kept.length === 1 ? 'One access is' : `${kept.length} accesses are`} being kept after they leave.
              The reason given goes onto the signed record.
            </Alert>
          </div>
        )}
      </Section>

      <Section title="Hardware to return" onEdit={() => onJumpToStep(OFF_STEP.return)} count={hardware.length}>
        <ItemList
          items={hardware.map((item) => ({
            key: item.catalogItemId,
            label: item.name,
            detail: item.assetDetails,
          }))}
          empty="Nothing listed for return."
        />
      </Section>

      <Section title="Data handover" onEdit={() => onJumpToStep(OFF_STEP.handover)}>
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Files to" value={e.dataHandoverTo} />
          <Row label="Shared mailboxes to" value={e.sharedMailboxHandoverTo} />
          <Row label="Teams and sites to" value={e.teamsHandoverTo} />
        </dl>
      </Section>

      <Section title="Special instructions" onEdit={() => onJumpToStep(OFF_STEP.notes)}>
        {draft.specialInstructions ? (
          <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {draft.specialInstructions}
          </p>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">None.</p>
        )}
      </Section>
    </>
  );
}

function Section({
  title,
  count,
  onEdit,
  children,
}: {
  title: string;
  count?: number;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-3 border-b border-slate-200 pb-2 dark:border-slate-800">
        <h3 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
          {title}
        </h3>
        {count !== undefined && <Badge tone="neutral">{count}</Badge>}
        <button
          type="button"
          onClick={onEdit}
          className="ml-auto text-xs font-medium text-brand-700 underline underline-offset-4 dark:text-brand-300"
        >
          Edit
        </button>
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, secret = false }: { label: string; value: string | null; secret?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className={value ? 'text-sm text-slate-900 dark:text-slate-100' : 'text-sm text-slate-500 dark:text-slate-400'}>
        {value || 'Not given'}
        {secret && value && (
          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400" title="Encrypted at rest">
            encrypted
          </span>
        )}
      </dd>
    </div>
  );
}

function ItemList({
  items,
  empty,
}: {
  items: { key: string; label: string; detail: string | null }[];
  empty: string;
}) {
  if (items.length === 0) return <p className="text-sm text-slate-500 dark:text-slate-400">{empty}</p>;

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.key} className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="text-slate-800 dark:text-slate-200">{item.label}</span>
          {item.detail && <span className="text-xs text-slate-500 dark:text-slate-400">: {item.detail}</span>}
        </li>
      ))}
    </ul>
  );
}
